/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useMemo, useState } from "react";

import { collection, getDocs } from "firebase/firestore";
import { geoEqualEarth, geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import countries110m from "world-atlas/countries-110m.json";
import { db, auth } from "../firebase";
import SpeciesPhotoModal from "./SpeciesPhotoModal";
import commonNameTagIcon from "../assets/common-name-tag.png";
import scientificNameTagIcon from "../assets/scientific-name-tag.png";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const WORLD_COUNTRIES = feature(
  countries110m,
  countries110m.objects.countries,
).features;

function asNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function cleanLocationPart(value) {
  return String(value || "").trim();
}

function normalizeLocationPart(value) {
  const normalized = cleanLocationPart(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\bprovincia de\b/g, "")
    .replace(/\bprovince\b/g, "")
    .replace(/\bdepartamento de\b/g, "")
    .replace(/\bdepartment\b/g, "")
    .replace(/\bdepartamento\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (normalized === "ciudad de mexico" || normalized === "mexico city") {
    return "ciudad de mexico";
  }

  return normalized;
}

function sameLocationPart(a, b) {
  return normalizeLocationPart(a) === normalizeLocationPart(b);
}

function parseDateKey(value) {
  const parts = String(value || "").split("-");
  if (parts.length !== 3) return null;

  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return null;

  const date = new Date(year, month - 1, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

function getPhotoDateValue(form) {
  const date = form?.date;

  if (date && typeof date === "object") {
    const day = date.day || "NA";
    const month = date.month || "NA";
    const year = date.year || "NA";

    if (day === "NA" || month === "NA" || year === "NA") return "";

    const key = `${year}-${month}-${day}`;
    return parseDateKey(key) ? key : "";
  }

  if (typeof date === "string") return parseDateKey(date) ? date : "";

  return "";
}

function getPhotoLocationLabel(form) {
  return (
    form?.locationName ||
    form?.location ||
    [form?.locationCity, form?.locationAdminArea, form?.locationCountry]
      .filter(Boolean)
      .join(", ")
  );
}

function getPhotoLocationData(form) {
  const lat = asNumber(form?.locationLat);
  const lng = asNumber(form?.locationLng);

  if (lat === null || lng === null) return null;

  const country = cleanLocationPart(form?.locationCountry) || "Unknown country";
  const city = cleanLocationPart(form?.locationCity);
  const rawAdminArea = cleanLocationPart(form?.locationAdminArea);

  const adminArea =
    sameLocationPart(country, "Colombia") && sameLocationPart(city, "Bogotá")
      ? "Bogotá"
      : rawAdminArea || "Unknown state/department";

  return {
    lat,
    lng,
    country,
    adminArea,
    city,
    name: getPhotoLocationLabel(form) || "Selected location",
  };
}

function LocationWorldMap({ points, selectedCountry }) {
  const width = 320;
  const height = 180;

  const selectedCountryFeature = WORLD_COUNTRIES.find((country) =>
    sameLocationPart(country?.properties?.name || "", selectedCountry),
  );

  const projection = (selectedCountryFeature ? geoMercator() : geoEqualEarth())
    .fitSize([width, height], selectedCountryFeature || { type: "Sphere" })
    .clipAngle(null);

  const path = geoPath(projection);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full rounded-[1.25rem] border border-[#93CA87]/40 bg-[#f4f6f8]"
      role="img"
      aria-label="Map of photo locations"
    >
      <rect width={width} height={height} fill="#EAF7F6" />

      {WORLD_COUNTRIES.map((country, index) => {
        const countryName = country?.properties?.name || "";
        const isSelected = sameLocationPart(countryName, selectedCountry);
        const hasPhotos = points.some((point) =>
          sameLocationPart(point.country, countryName),
        );

        return (
          <path
            key={index}
            d={path(country)}
            fill={isSelected ? "#24C3BE" : hasPhotos ? "#CFF4EF" : "#FCFFF6"}
            fillOpacity={isSelected ? "0.36" : "1"}
            stroke={isSelected ? "#24C3BE" : "#374049"}
            strokeOpacity={isSelected ? "0.95" : "0.35"}
            strokeWidth={isSelected ? "0.9" : "0.45"}
          />
        );
      })}

      {points.map((point, index) => {
        const projected = projection([point.lng, point.lat]);
        if (!projected) return null;

        return (
          <circle
            key={`${point.lat}-${point.lng}-${index}`}
            cx={projected[0]}
            cy={projected[1]}
            r="1.5"
            strokeWidth="0.4"
            fill="#24C3BE"
            stroke="#374049"
          />
        );
      })}
    </svg>
  );
}

export default function ExplorePhotoModes({
  mode,
  dataUID,
  routeUID,
  isAdminCollectionMode = false,
}) {
  const [photoRecords, setPhotoRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(0);
  const [selectedDate, setSelectedDate] = useState("");
  const [showNoDatePhotos, setShowNoDatePhotos] = useState(false);
  const [calendarYear, setCalendarYear] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedAdminArea, setSelectedAdminArea] = useState("");
  const [selectedLetter, setSelectedLetter] = useState("A");
  const [alphaSort, setAlphaSort] = useState("scientific");
  const [selected, setSelected] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [previewPhotoBySpecies, setPreviewPhotoBySpecies] = useState({});

  const currentUID = auth.currentUser?.uid;
  const isOwnCollection = dataUID === currentUID;

  function refreshPhotoRecords() {
    setPhotoRefreshKey((currentKey) => currentKey + 1);
  }

  useEffect(() => {
    let cancelled = false;

    async function loadPhotos() {
      if (!dataUID) {
        setPhotoRecords([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const snap = await getDocs(collection(db, "users", dataUID, "species"));
        const rows = [];

        snap.forEach((docSnap) => {
          const species = { id: docSnap.id, ...docSnap.data() };
          const forms = Array.isArray(species.forms) ? species.forms : [];

          forms.forEach((form, formIndex) => {
            if (!form?.url) return;

            rows.push({
              species,
              form,
              formIndex,
              dateKey: getPhotoDateValue(form),
              locationData: getPhotoLocationData(form),
            });
          });
        });

        if (!cancelled) setPhotoRecords(rows);
      } catch (err) {
        console.error("Failed to load photo modes:", err);
        if (!cancelled) setPhotoRecords([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPhotos();

    return () => {
      cancelled = true;
    };
  }, [dataUID, photoRefreshKey]);

  const availableDates = useMemo(() => {
    return [
      ...new Set(photoRecords.map((record) => record.dateKey).filter(Boolean)),
    ].sort((a, b) => new Date(a) - new Date(b));
  }, [photoRecords]);

  const dateCounts = useMemo(() => {
    const counts = {};

    photoRecords.forEach((record) => {
      if (!record.dateKey) return;
      counts[record.dateKey] = (counts[record.dateKey] || 0) + 1;
    });

    return counts;
  }, [photoRecords]);

  const availableYears = useMemo(() => {
    return [
      ...new Set(
        availableDates
          .map((dateKey) => parseDateKey(dateKey)?.getFullYear())
          .filter(Boolean),
      ),
    ];
  }, [availableDates]);

  const availableMonthsByYear = useMemo(() => {
    const grouped = {};

    availableDates.forEach((dateKey) => {
      const date = parseDateKey(dateKey);
      if (!date) return;

      const year = date.getFullYear();
      const month = date.getMonth();

      if (!grouped[year]) grouped[year] = [];
      if (!grouped[year].includes(month)) grouped[year].push(month);
    });

    Object.keys(grouped).forEach((year) => {
      grouped[year].sort((a, b) => a - b);
    });

    return grouped;
  }, [availableDates]);

  useEffect(() => {
    if (mode !== "date") return;
    if (selectedDate || availableDates.length === 0) return;

    const mostRecentDate = parseDateKey(
      availableDates[availableDates.length - 1],
    );

    if (!mostRecentDate) return;

    setSelectedDate(toDateKey(mostRecentDate));
    setCalendarYear(mostRecentDate.getFullYear());
    setCalendarMonth(mostRecentDate.getMonth());
  }, [mode, selectedDate, availableDates]);

  const calendarCells = useMemo(() => {
    if (calendarYear === null || calendarMonth === null) return [];

    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const leadingBlanks = firstDay.getDay();
    const cells = [];

    for (let i = 0; i < leadingBlanks; i++) cells.push(null);

    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(toDateKey(new Date(calendarYear, calendarMonth, day)));
    }

    while (cells.length % 7 !== 0) cells.push(null);

    return cells;
  }, [calendarYear, calendarMonth]);

  const dateRecords = useMemo(() => {
    if (showNoDatePhotos) {
      return photoRecords.filter((record) => !record.dateKey);
    }

    if (!selectedDate) return [];
    return photoRecords.filter((record) => record.dateKey === selectedDate);
  }, [photoRecords, selectedDate, showNoDatePhotos]);

  const countries = useMemo(() => {
    const set = new Set();

    photoRecords.forEach((record) => {
      if (!record.locationData?.country) return;

      const exists = [...set].some((country) =>
        sameLocationPart(country, record.locationData.country),
      );

      if (!exists) set.add(record.locationData.country);
    });

    return [...set].sort((a, b) => a.localeCompare(b));
  }, [photoRecords]);

  const adminAreasByCountry = useMemo(() => {
    const grouped = {};

    photoRecords.forEach((record) => {
      const locationData = record.locationData;
      if (!locationData?.country) return;

      if (!grouped[locationData.country]) grouped[locationData.country] = [];

      const exists = grouped[locationData.country].some((adminArea) =>
        sameLocationPart(adminArea, locationData.adminArea),
      );

      if (!exists) grouped[locationData.country].push(locationData.adminArea);
    });

    Object.keys(grouped).forEach((country) => {
      grouped[country].sort((a, b) => a.localeCompare(b));
    });

    return grouped;
  }, [photoRecords]);

  useEffect(() => {
    if (mode !== "location") return;
    if (selectedCountry || countries.length === 0) return;

    const firstCountry = countries[0];
    const firstAdminArea = adminAreasByCountry[firstCountry]?.[0] || "";

    setSelectedCountry(firstCountry);
    setSelectedAdminArea(firstAdminArea);
  }, [mode, selectedCountry, countries, adminAreasByCountry]);

  const locationRecords = useMemo(() => {
    if (!selectedCountry || !selectedAdminArea) return [];

    return photoRecords.filter((record) => {
      const locationData = record.locationData;
      if (!locationData) return false;

      return (
        sameLocationPart(locationData.country, selectedCountry) &&
        sameLocationPart(locationData.adminArea, selectedAdminArea)
      );
    });
  }, [photoRecords, selectedCountry, selectedAdminArea]);

  const locationPoints = useMemo(() => {
    return locationRecords.map((record) => record.locationData).filter(Boolean);
  }, [locationRecords]);

  const alphaSpeciesRecords = useMemo(() => {
    const speciesMap = new Map();

    photoRecords.forEach((record) => {
      if (!record.species?.id) return;

      if (!speciesMap.has(record.species.id)) {
        speciesMap.set(record.species.id, {
          species: record.species,
          forms: [],
        });
      }

      speciesMap.get(record.species.id).forms.push({
        ...record.form,
        formIndex: record.formIndex,
        locationData: record.locationData,
      });
    });

    return [...speciesMap.values()]
      .filter((record) => {
        const value =
          alphaSort === "common"
            ? record.species.common || ""
            : record.species.name || "";

        return value.toUpperCase().startsWith(selectedLetter);
      })
      .sort((a, b) => {
        const av =
          alphaSort === "common"
            ? a.species.common || a.species.name || ""
            : a.species.name || "";

        const bv =
          alphaSort === "common"
            ? b.species.common || b.species.name || ""
            : b.species.name || "";

        return av.localeCompare(bv);
      });
  }, [photoRecords, selectedLetter, alphaSort]);

  function selectDate(dateKey) {
    const date = parseDateKey(dateKey);
    if (!date) return;

    setShowNoDatePhotos(false);
    setSelectedDate(dateKey);
    setCalendarYear(date.getFullYear());
    setCalendarMonth(date.getMonth());
  }

  function moveToAvailableDate(direction) {
    if (availableDates.length === 0) return;

    const currentIndex = availableDates.indexOf(selectedDate);
    const fallbackIndex = direction > 0 ? 0 : availableDates.length - 1;

    const nextIndex =
      currentIndex === -1
        ? fallbackIndex
        : Math.min(
            Math.max(currentIndex + direction, 0),
            availableDates.length - 1,
          );

    selectDate(availableDates[nextIndex]);
  }

  function jumpToYear(year) {
    const firstDateInYear = availableDates.find((dateKey) => {
      const date = parseDateKey(dateKey);
      return date?.getFullYear() === year;
    });

    if (firstDateInYear) selectDate(firstDateInYear);
  }

  function jumpToMonth(month) {
    const firstDateInMonth = availableDates.find((dateKey) => {
      const date = parseDateKey(dateKey);
      return date?.getFullYear() === calendarYear && date?.getMonth() === month;
    });

    if (firstDateInMonth) selectDate(firstDateInMonth);
  }

  function selectCountry(country) {
    const firstAdminArea = adminAreasByCountry[country]?.[0] || "";
    setSelectedCountry(country);
    setSelectedAdminArea(firstAdminArea);
  }

  function AlphaSpeciesGrid({ records, emptyText }) {
    if (records.length === 0) {
      return (
        <div className="col-span-full rounded-2xl bg-white border border-[#93CA87]/40 p-8 text-center text-sm text-slate-500">
          {emptyText}
        </div>
      );
    }

    return records.map((record) => {
      const forms = Array.isArray(record.forms) ? record.forms : [];
      const activeIndex = previewPhotoBySpecies[record.species.id] ?? 0;
      const activeForm = forms[activeIndex] || forms[0];

      return (
        <button
          key={record.species.id}
          type="button"
          onClick={() => {
            setSelected(record.species);
            setPhotoIndex(activeForm?.formIndex || 0);
          }}
          className="relative bg-white rounded-b-2xl border shadow-sm transition w-full max-w-64 flex flex-col items-center text-center cursor-pointer overflow-visible z-0 border-[#93CA87]/40 hover:shadow-md hover:-translate-y-[2px]"
        >
          <div className="relative w-full aspect-[4/3] flex items-center justify-center bg-white">
            <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
              <img
                src={activeForm?.url}
                alt={record.species.name || "Species"}
                className="absolute max-w-full max-h-full object-contain"
                loading="lazy"
              />
            </div>

            {forms.length > 1 && (
              <div
                className="absolute left-2 bottom-2 z-20 flex justify-start gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {forms.map((form, formIndex) => (
                  <button
                    key={form.url || formIndex}
                    type="button"
                    onClick={() =>
                      setPreviewPhotoBySpecies((prev) => ({
                        ...prev,
                        [record.species.id]: formIndex,
                      }))
                    }
                    className={`w-5 h-5 rounded overflow-hidden border bg-white/80 backdrop-blur-sm transition ${
                      formIndex === activeIndex
                        ? "border-[#24C3BE] ring-2 ring-[#24C3BE]/30"
                        : "border-white/70 hover:border-[#24C3BE]/70"
                    }`}
                  >
                    <img
                      src={form.url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-full min-w-0 px-2.5 pt-3 pb-4 sm:px-3 sm:pb-5">
            {alphaSort === "common" ? (
              <>
                <h3
                  className="w-full min-w-0 truncate text-[clamp(0.78rem,3.6vw,1.125rem)] leading-tight text-[#374049] sm:text-[clamp(0.9rem,1.8vw,1.125rem)]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                  title={record.species.common || "Unknown Common Name"}
                >
                  {record.species.common || "Unknown Common Name"}
                </h3>

                <p className="mt-0.5 w-full min-w-0 truncate text-[clamp(0.65rem,2.8vw,0.875rem)] leading-tight italic text-[#374049]/60 sm:text-[clamp(0.75rem,1.35vw,0.875rem)]">
                  {record.species.name || "—"}
                </p>
              </>
            ) : (
              <>
                <h3
                  className="w-full min-w-0 truncate text-[clamp(0.78rem,3.6vw,1.125rem)] leading-tight italic text-[#374049] sm:text-[clamp(0.9rem,1.8vw,1.125rem)]"
                  style={{ fontFamily: "Cormorant Garamond" }}
                  title={record.species.name || "—"}
                >
                  {record.species.name || "—"}
                </h3>

                <p className="mt-0.5 w-full min-w-0 truncate text-[clamp(0.65rem,2.8vw,0.875rem)] leading-tight text-[#374049]/60 sm:text-[clamp(0.75rem,1.35vw,0.875rem)]">
                  {record.species.common || ""}
                </p>
              </>
            )}
          </div>
        </button>
      );
    });
  }

  function PhotoGrid({ records, emptyText }) {
    if (records.length === 0) {
      return (
        <div className="col-span-full rounded-2xl bg-white border border-[#93CA87]/40 p-8 text-center text-sm text-slate-500">
          {emptyText}
        </div>
      );
    }

    return records.map((record) => (
      <button
        key={`${record.species.id}-${record.form.id || record.form.url}-${record.formIndex}`}
        type="button"
        onClick={() => {
          setSelected(record.species);
          setPhotoIndex(record.formIndex || 0);
        }}
        className="relative bg-white rounded-b-2xl border shadow-sm transition w-full max-w-64 flex flex-col items-center text-center cursor-pointer overflow-visible z-0 border-[#93CA87]/40 hover:shadow-md hover:-translate-y-[2px]"
      >
        <div className="relative w-full aspect-[4/3] flex items-center justify-center bg-white">
          <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
            <img
              src={record.form.url}
              alt={record.species.name || "Species"}
              className="absolute max-w-full max-h-full object-contain"
              loading="lazy"
            />
          </div>
        </div>

        <div className="pt-3 pb-5 px-3">
          {mode === "alpha" && alphaSort === "common" ? (
            <>
              <h3
                className="w-full max-w-52 truncate text-lg text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
                title={record.species.common || "Unknown Common Name"}
              >
                {record.species.common || "Unknown Common Name"}
              </h3>

              <p className="max-w-52 truncate text-sm italic text-[#374049]/60">
                {record.species.name || "—"}
              </p>
            </>
          ) : (
            <>
              <h3
                className="w-full max-w-52 truncate text-lg italic text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
                title={record.species.name || "—"}
              >
                {record.species.name || "—"}
              </h3>

              <p className="text-[#374049]/60 text-sm">
                {record.species.common || ""}
              </p>
            </>
          )}
          {record.locationData?.name && (
            <p className="mt-1 text-xs text-slate-500 truncate max-w-52">
              ⌖ {record.locationData.name}
            </p>
          )}
        </div>
      </button>
    ));
  }

  if (loading) {
    return (
      <div className="flex justify-center pt-6">
        <div className="bg-white rounded-2xl border border-[#93CA87]/40 shadow-sm w-56 h-56 flex flex-col items-center justify-center text-center">
          <div className="w-6 h-6 border-2 border-[#24C3BE] border-t-transparent rounded-full animate-spin mb-3" />

          <div
            className="text-lg text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Loading Photos
          </div>
        </div>
      </div>
    );
  }

  if (mode === "alpha") {
    return (
      <div className="mx-auto max-w-6xl px-4 pt-6 pb-10">
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="overflow-x-auto bg-white border border-[#93CA87]/40 rounded-2xl shadow-sm px-4 py-3 mx-auto w-fit max-w-full">
            <div className="flex flex-wrap items-center justify-center gap-1.5">
              {LETTERS.map((letter) => (
                <button
                  key={letter}
                  type="button"
                  onClick={() => setSelectedLetter(letter)}
                  className={`w-7 h-7 rounded-full text-[11px] font-medium transition ${
                    selectedLetter === letter
                      ? "bg-[#24C3BE] text-white shadow-sm"
                      : "bg-white text-[#374049] border border-[#93CA87]/40 hover:bg-[#24C3BE]/10 hover:text-[#24C3BE]"
                  }`}
                >
                  {letter}
                </button>
              ))}

              <div className="ml-2 flex rounded-full border border-[#93CA87]/40 bg-[#f4f6f8] p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setAlphaSort("scientific")}
                  className={`h-7 min-w-9 rounded-full px-2 text-xs font-medium transition ${
                    alphaSort === "scientific"
                      ? "bg-[#24C3BE] text-white shadow-sm"
                      : "text-[#374049]/60 hover:bg-white hover:text-[#24C3BE]"
                  }`}
                  title="Sort by scientific name"
                >
                  <img
                    src={scientificNameTagIcon}
                    alt=""
                    className="h-5 w-10 object-contain"
                  />{" "}
                </button>

                <button
                  type="button"
                  onClick={() => setAlphaSort("common")}
                  className={`h-7 min-w-9 rounded-full px-2 text-xs font-medium transition ${
                    alphaSort === "common"
                      ? "bg-[#24C3BE] text-white shadow-sm"
                      : "text-[#374049]/60 hover:bg-white hover:text-[#24C3BE]"
                  }`}
                  title="Sort by common name"
                >
                  <img
                    src={commonNameTagIcon}
                    alt=""
                    className="h-5 w-10 object-contain"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-6 sm:gap-8 mx-auto grid-cols-2 lg:grid-cols-4 justify-items-center items-start">
          {" "}
          <AlphaSpeciesGrid
            records={alphaSpeciesRecords}
            emptyText={`No species found under ${selectedLetter}.`}
          />
        </div>

        <SpeciesPhotoModal
          selected={selected}
          setSelected={setSelected}
          photoIndex={photoIndex}
          setPhotoIndex={setPhotoIndex}
          dataUID={dataUID}
          currentUID={currentUID}
          uid={routeUID}
          isOwnCollection={isOwnCollection}
          isAdminCollectionMode={isAdminCollectionMode}
          onRefresh={refreshPhotoRecords}
          setSpeciesList={(updater) => {
            setPhotoRecords((prevRecords) => {
              const speciesMap = new Map();

              prevRecords.forEach((record) => {
                speciesMap.set(record.species.id, record.species);
              });

              const previousSpeciesList = [...speciesMap.values()];
              const nextSpeciesList =
                typeof updater === "function"
                  ? updater(previousSpeciesList)
                  : updater;

              const nextSpeciesMap = new Map(
                nextSpeciesList.map((species) => [species.id, species]),
              );

              return prevRecords
                .map((record) => {
                  const updatedSpecies = nextSpeciesMap.get(record.species.id);

                  if (!updatedSpecies) return null;

                  const updatedForm =
                    updatedSpecies.forms?.[record.formIndex] || record.form;

                  return {
                    ...record,
                    species: updatedSpecies,
                    form: updatedForm,
                    dateKey: getPhotoDateValue(updatedForm),
                    locationData: getPhotoLocationData(updatedForm),
                  };
                })
                .filter(Boolean);
            });
          }}
        />
      </div>
    );
  }

  if (mode === "date") {
    return (
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-4 px-4 pt-6 pb-10 lg:grid-cols-[20rem_1fr]">
        {" "}
        <div className="self-start rounded-2xl border border-[#93CA87]/40 bg-white p-3 shadow-sm">
          {" "}
          <div className="mb-4 flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setShowNoDatePhotos(true)}
              className={`rounded-full border px-4 py-2 text-xs font-medium shadow-sm transition ${
                showNoDatePhotos
                  ? "bg-[#24C3BE] border-[#24C3BE] text-white"
                  : "bg-white border-[#93CA87]/40 text-[#374049] hover:bg-[#24C3BE]/10 hover:text-[#24C3BE]"
              }`}
            >
              No date
            </button>
            <select
              value={calendarYear || ""}
              onChange={(e) => jumpToYear(Number(e.target.value))}
              disabled={availableYears.length === 0}
              className="rounded-full border border-[#93CA87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/25"
            >
              <option value="" disabled>
                Year
              </option>

              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            <select
              value={calendarMonth ?? ""}
              onChange={(e) => jumpToMonth(Number(e.target.value))}
              disabled={!calendarYear}
              className="rounded-full border border-[#93CA87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/25"
            >
              <option value="" disabled>
                Month
              </option>

              {MONTH_NAMES.map((monthName, month) => {
                const hasPhotos = (
                  availableMonthsByYear[calendarYear] || []
                ).includes(month);

                return (
                  <option key={month} value={month} disabled={!hasPhotos}>
                    {monthName}
                  </option>
                );
              })}
            </select>
          </div>
          {calendarYear !== null && calendarMonth !== null ? (
            <div className="mx-auto max-w-xs rounded-[1.5rem] border border-[#93CA87]/40 bg-[#f4f6f8] p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => moveToAvailableDate(-1)}
                  disabled={
                    availableDates.indexOf(selectedDate) <= 0 &&
                    !showNoDatePhotos
                  }
                  className="rounded-full bg-white border border-[#93CA87]/40 px-2.5 py-1.5 text-[11px] font-medium text-[#374049] hover:bg-[#24C3BE]/10 disabled:opacity-40 disabled:hover:bg-white transition"
                >
                  ‹ Previous date
                </button>

                <div className="w-6" />

                <button
                  type="button"
                  onClick={() => moveToAvailableDate(1)}
                  disabled={
                    availableDates.indexOf(selectedDate) >=
                      availableDates.length - 1 && !showNoDatePhotos
                  }
                  className="rounded-full bg-white border border-[#93CA87]/40 px-2.5 py-1.5 text-[11px] font-medium text-[#374049] hover:bg-[#24C3BE]/10 disabled:opacity-40 disabled:hover:bg-white transition"
                >
                  Next date ›
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1.5 text-center">
                {WEEK_DAYS.map((day) => (
                  <div
                    key={day}
                    className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
                  >
                    {day}
                  </div>
                ))}

                {calendarCells.map((dateKey, index) => {
                  if (!dateKey) {
                    return (
                      <div key={`blank-${index}`} className="aspect-square" />
                    );
                  }

                  const date = parseDateKey(dateKey);
                  const count = dateCounts[dateKey] || 0;
                  const isSelectable = count > 0;
                  const isSelected =
                    dateKey === selectedDate && !showNoDatePhotos;
                  return (
                    <button
                      key={dateKey}
                      type="button"
                      disabled={!isSelectable}
                      onClick={() => selectDate(dateKey)}
                      className={`aspect-square rounded-xl border text-[11px] transition flex flex-col items-center justify-center ${
                        isSelected
                          ? "bg-[#24C3BE] border-[#24C3BE] text-white shadow-sm"
                          : isSelectable
                            ? "bg-white border-[#93CA87]/40 text-[#374049] hover:bg-[#24C3BE]/10 hover:text-[#24C3BE]"
                            : "bg-white/50 border-transparent text-slate-300 cursor-not-allowed"
                      }`}
                    >
                      <span className="text-xs font-semibold">
                        {date.getDate()}
                      </span>

                      {isSelectable && (
                        <span
                          className={`mt-0.5 text-[10px] ${
                            isSelected ? "text-white/80" : "text-slate-500"
                          }`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="rounded-2xl bg-[#f4f6f8] border border-[#93CA87]/40 p-8 text-center text-sm text-slate-500">
              No dated photos available.
            </div>
          )}
        </div>
        <div className="grid gap-6 sm:gap-8 mx-auto grid-cols-1 sm:grid-cols-2 justify-items-center items-start lg:grid-cols-2 xl:grid-cols-3">
          <PhotoGrid
            records={dateRecords}
            emptyText="Select a date with photos."
          />
        </div>
        <SpeciesPhotoModal
          selected={selected}
          setSelected={setSelected}
          photoIndex={photoIndex}
          setPhotoIndex={setPhotoIndex}
          dataUID={dataUID}
          currentUID={currentUID}
          uid={routeUID}
          isOwnCollection={isOwnCollection}
          isAdminCollectionMode={isAdminCollectionMode}
          onRefresh={refreshPhotoRecords}
          setSpeciesList={(updater) => {
            setPhotoRecords((prevRecords) => {
              const speciesMap = new Map();

              prevRecords.forEach((record) => {
                speciesMap.set(record.species.id, record.species);
              });

              const previousSpeciesList = [...speciesMap.values()];
              const nextSpeciesList =
                typeof updater === "function"
                  ? updater(previousSpeciesList)
                  : updater;

              const nextSpeciesMap = new Map(
                nextSpeciesList.map((species) => [species.id, species]),
              );

              return prevRecords
                .map((record) => {
                  const updatedSpecies = nextSpeciesMap.get(record.species.id);

                  if (!updatedSpecies) return null;

                  const updatedForm =
                    updatedSpecies.forms?.[record.formIndex] || record.form;

                  return {
                    ...record,
                    species: updatedSpecies,
                    form: updatedForm,
                    dateKey: getPhotoDateValue(updatedForm),
                    locationData: getPhotoLocationData(updatedForm),
                  };
                })
                .filter(Boolean);
            });
          }}
        />{" "}
      </div>
    );
  }

  if (mode === "location") {
    return (
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-4 px-4 pt-6 pb-10 lg:grid-cols-[20rem_1fr]">
        {" "}
        <div className="rounded-2xl border border-[#93CA87]/40 bg-white p-3 shadow-sm">
          <div className="mb-3 flex flex-wrap justify-center gap-2">
            <select
              value={selectedCountry}
              onChange={(e) => selectCountry(e.target.value)}
              disabled={countries.length === 0}
              className="rounded-full border border-[#93CA87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/25"
            >
              <option value="" disabled>
                Country
              </option>

              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>

            <select
              value={selectedAdminArea}
              onChange={(e) => setSelectedAdminArea(e.target.value)}
              disabled={!selectedCountry}
              className="rounded-full border border-[#93CA87]/40 bg-[#f4f6f8] px-3 py-2 text-xs font-medium text-[#374049] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#24C3BE]/25"
            >
              <option value="" disabled>
                State/Department
              </option>

              {(adminAreasByCountry[selectedCountry] || []).map((adminArea) => (
                <option key={adminArea} value={adminArea}>
                  {adminArea}
                </option>
              ))}
            </select>
          </div>

          {locationPoints.length > 0 ? (
            <>
              <LocationWorldMap
                points={locationPoints}
                selectedCountry={selectedCountry}
              />

              <div className="mt-2 text-center text-xs text-slate-500">
                {locationPoints.length} mapped photo
                {locationPoints.length === 1 ? "" : "s"}
              </div>
            </>
          ) : (
            <div className="rounded-2xl bg-[#f4f6f8] border border-[#93CA87]/40 p-8 text-center text-sm text-slate-500">
              No mapped photos available.
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mx-auto justify-items-center items-start">
          <PhotoGrid
            records={locationRecords}
            emptyText="Select a location with photos."
          />
        </div>
        <SpeciesPhotoModal
          selected={selected}
          setSelected={setSelected}
          photoIndex={photoIndex}
          setPhotoIndex={setPhotoIndex}
          dataUID={dataUID}
          currentUID={currentUID}
          uid={routeUID}
          isOwnCollection={isOwnCollection}
          isAdminCollectionMode={isAdminCollectionMode}
          onRefresh={refreshPhotoRecords}
          setSpeciesList={(updater) => {
            setPhotoRecords((prevRecords) => {
              const speciesMap = new Map();

              prevRecords.forEach((record) => {
                speciesMap.set(record.species.id, record.species);
              });

              const previousSpeciesList = [...speciesMap.values()];
              const nextSpeciesList =
                typeof updater === "function"
                  ? updater(previousSpeciesList)
                  : updater;

              const nextSpeciesMap = new Map(
                nextSpeciesList.map((species) => [species.id, species]),
              );

              return prevRecords
                .map((record) => {
                  const updatedSpecies = nextSpeciesMap.get(record.species.id);

                  if (!updatedSpecies) return null;

                  const updatedForm =
                    updatedSpecies.forms?.[record.formIndex] || record.form;

                  return {
                    ...record,
                    species: updatedSpecies,
                    form: updatedForm,
                    dateKey: getPhotoDateValue(updatedForm),
                    locationData: getPhotoLocationData(updatedForm),
                  };
                })
                .filter(Boolean);
            });
          }}
        />{" "}
      </div>
    );
  }

  return null;
}
